import { type components, type SequenceAnalyzerFeatureType, TrackEnum } from "@selector/shared/openapi";
import { clamp } from "lodash-es";
import type { StateCreator } from "zustand";

import type {
  FeaturesGroups,
  Range,
  SelectionRange,
  SessionRequest,
  Track,
  TrackType,
  ZoomRange,
} from "../../types.ts";
import { getSequenceAnalyzerFeatureTypeFromTrackType } from "../../utils/type-conversion.ts";
import type { GenomeViewerStore } from "../store.ts";
import { handleScrollingEnd } from "./user-events-slice.ts";

export interface SelectionState {
  selectedRange?: SelectionRange;
  zoomRange?: ZoomRange;
  /** Overview visible area box */
  isDraggingRegionIndicator: boolean;
  leftSelectionDragging: boolean;
  rightSelectionDragging: boolean;
  leftSelectionHovering: boolean;
  rightSelectionHovering: boolean;
  trackGroupsInSelection: string[];
  fullRangeSelection: boolean;
  featureSelection: Map<string, number[]>; // Map of <TrackId, featureId[]>
  organismSelection: number[]; // List of orgId
  isSelecting: boolean;
  latestSelectedFeature: { featureId: number; trackType: TrackType; subTypeId?: number } | undefined;
}

export interface RangePerContig extends Range {
  contigId: number;
  /** Hex color strings */
  trackColors?: components["schemas"]["TrackColors"];
}

export interface SelectionActions {
  zoomToRange: () => void;
  getSelectedRange: () => RangePerContig[] | null;
  toggleFullRangeSelection: () => void;
  clearRangeSelection: () => void;
  getFeaturesSessionRequest: () => Map<SequenceAnalyzerFeatureType, SessionRequest> | null;
  getFeaturesGroups: (featureSelection?: Map<string, number[]>) => FeaturesGroups;
  getVisibleRange: (groupId: string) => { start: number; stop: number };
}

export type SelectionSlice = SelectionState & SelectionActions;

export const initialSelectionState = {
  selectedRange: undefined,
  zoomRange: undefined,
  isDraggingRegionIndicator: false,
  leftSelectionDragging: false,
  rightSelectionDragging: false,
  leftSelectionHovering: false,
  rightSelectionHovering: false,
  trackGroupsInSelection: [],
  fullRangeSelection: true, // select all by default
  featureSelection: new Map(),
  organismSelection: [],
  isSelecting: false,
  latestSelectedFeature: undefined,
};

export const createSelectionSlice: StateCreator<GenomeViewerStore, [], [], SelectionSlice> = (set, get) => ({
  ...initialSelectionState,
  zoomToRange() {
    const region = get().zoomRange;
    const canvas = get().canvas;
    if (!region || !canvas) return;

    const minX = Math.min(region.startX, region.stopX);
    const maxX = Math.max(region.startX, region.stopX);

    const zoomFactor = (maxX - minX) / canvas.width;

    get().handleZoom(zoomFactor, minX, true);

    handleScrollingEnd(set);
    // Clear the rectangle
    set({
      zoomRange: undefined,
      isScrolling: true,
    });
  },

  getSelectedRange() {
    let region: Range | undefined = get().selectedRange;
    // In the case where no region is selected, try to send the full visible region on screen
    const sendFullVisibleRegion = !region;

    if (!region) {
      region = {
        start: Math.floor(get().scrollX * get().basePerPixel),
        stop: Math.ceil((get().scrollX + get().canvas!.width) * get().basePerPixel),
      };
    }

    const start = Math.min(region.start, region.stop);
    const stop = Math.max(region.start, region.stop);

    const selection: RangePerContig[] = [];

    [...get().trackGroups.entries()]
      .filter(
        ([groupId, trackGroup]) =>
          trackGroup.graphics.isVisible && (sendFullVisibleRegion || get().trackGroupsInSelection.includes(groupId)),
      )
      .forEach(([groupId, trackGroup]) => {
        const startRange = clamp(start - trackGroup.graphics.baseShift, 0, trackGroup.graphics.length) + 1; // +1 because they are 1-based indexing
        const stopRange = clamp(stop - trackGroup.graphics.baseShift, 1, trackGroup.graphics.length);
        const activeTracks = [...get().tracks.values()].filter((track) => track.groupId === groupId);

        if (startRange <= stopRange) {
          const geneTrack = activeTracks.find((track) => track.trackType === TrackEnum.GENE);
          const proteinTrack = activeTracks.find((track) => track.trackType === TrackEnum.PROTEIN_LOCATION);
          const transcriptTrack = activeTracks.find((track) => track.trackType === TrackEnum.TRANSCRIPT_LOCATION);
          const nuFeatureTracks = activeTracks.filter((track) => track.trackType === TrackEnum.NUCLEOTIDE_FEATURE);

          selection.push({
            contigId: trackGroup.contigId,
            start: startRange,
            stop: stopRange,
            trackColors: {
              gene: geneTrack?.color,
              protein: proteinTrack?.color,
              transcript: transcriptTrack?.color,
              nuFeatures: [
                ...nuFeatureTracks.map((track: Track) => ({
                  featureTypeId: track.subType!.id,
                  color: track.color,
                })),
              ],
            },
          });
        }
      });

    return selection.length > 0 ? selection : null;
  },
  toggleFullRangeSelection() {
    const newFullRangeSelection = !get().fullRangeSelection;
    const newTrackGroupsInSelection = newFullRangeSelection
      ? [...get().trackGroups.keys()]
      : get().trackGroupsInSelection;
    set({
      fullRangeSelection: newFullRangeSelection,
      trackGroupsInSelection: newTrackGroupsInSelection,
    });
    // Update the selected features when selecting all trackGroups
    get().postTrackSelectionMessages(newTrackGroupsInSelection, get().selectedRange);
  },
  clearRangeSelection() {
    set({
      selectedRange: undefined,
      featureSelection: new Map(),
    });
    get().postTrackSelectionMessages(get().trackGroupsInSelection, undefined);
    get().refreshMain();
    get().refreshTracks();
  },
  getFeaturesSessionRequest() {
    const featuresMap = get().getFeaturesGroups(get().featureSelection);
    const payload = new Map<SequenceAnalyzerFeatureType, SessionRequest>();

    for (const [trackType, featureGroups] of featuresMap) {
      const featureType = getSequenceAnalyzerFeatureTypeFromTrackType(trackType);
      if (!featureType) {
        continue; // In the case of Pangenome
      }

      payload.set(featureType, {
        featureType,
        sequenceIds: featureGroups.flatMap((group) => group.featureIds),
      });
    }

    return payload;
  },
  getFeaturesGroups(featureSelection = get().featureSelection) {
    const featuresMap: FeaturesGroups = new Map();

    for (const [trackId, featureIds] of featureSelection.entries()) {
      const track = get().tracks.get(trackId);
      if (!track) continue;

      const trackGroup = get().trackGroups.get(track.groupId);
      if (!trackGroup || !track.isVisible || !trackGroup.graphics.isVisible) continue;
      const trackType = track.trackType;
      const subType = track.subType;

      const existingFeatureGroup = featuresMap.get(trackType);
      if (existingFeatureGroup) {
        const existingSubTypeGroup = existingFeatureGroup.find((group) => group.subType?.id === subType?.id);
        // Keep feature ids uniques
        if (existingSubTypeGroup) {
          existingSubTypeGroup.featureIds = [...new Set(existingSubTypeGroup.featureIds.concat(featureIds))];
        } else {
          existingFeatureGroup.push({
            subType,
            featureIds,
          });
        }
        featuresMap.set(trackType, existingFeatureGroup);
      } else {
        featuresMap.set(trackType, [
          {
            subType,
            featureIds,
          },
        ]);
      }
    }

    return featuresMap;
  },
  getVisibleRange(groupId) {
    const trackGroup = get().trackGroups.get(groupId);
    if (!trackGroup)
      return {
        start: 0,
        stop: 0,
      };

    return {
      start: Math.floor(get().scrollX * get().basePerPixel) - trackGroup.graphics.baseShift,
      stop: Math.ceil((get().scrollX + get().canvas!.width) * get().basePerPixel) - trackGroup.graphics.baseShift,
    };
  },
});
